import React, { useCallback, useEffect, useMemo, useState } from 'react'
import type {
  IDropdownOption,
  IDropdownProps,
  TDropdownListFooterProps,
  TOnChangeDropdownItem,
} from '@frontend/pole-ui/lib/components/Dropdown'
import { Dropdown as PoleUIDropdown } from '@frontend/pole-ui/lib/components/Dropdown'
import AddingLocationButton from '@/components/composed/modals/ChoosingLocationModal/components/AddingLocationButton/AddingLocationButton'
import type { IStoreLocation, IStoreWithComment } from '@/types'
import { useUserStores } from '@/utils/hooks/useUserStores'
import { CONSIGNOR_PARTS_SEPARATOR } from './constants'
import type { IConsignorDropdownProps } from './interfaces'
import './ConsignorDropdown.scss'

const SYNTHETIC_VALUE = '__consignor_selected_text__'

const normalizeWhitespace = (text?: string) => (text || '').replace(/\s+/g, ' ').trim()

const buildComposedText = (store: IStoreWithComment) =>
  [store.name, store.location?.address, store.comment].filter(Boolean).join(CONSIGNOR_PARTS_SEPARATOR).replace(/\s+/g, ' ').trim()

const createOptionFromStore = (store: IStoreWithComment, fallbackLabel?: string): IDropdownOption<string> => {
  const storeName = store.name || fallbackLabel || ''
  const storeAddress = store.location?.address || ''
  const storeComment = store.comment || ''
  const value = String(store.location?.id || store.id)
  const composed = buildComposedText(store)
  const secondLine = [storeAddress, storeComment].filter(Boolean).join(CONSIGNOR_PARTS_SEPARATOR).replace(/\s+/g, ' ').trim()

  return {
    value,
    label: (
      <span className="consignorDropdown__option">
        {storeName ? <strong className="consignorDropdown__optionName">{storeName}</strong> : null}
        {secondLine ? <div className="consignorDropdown__optionDetails">{secondLine}</div> : null}
      </span>
    ),
    payload: {
      composed,
    },
  }
}

const getOptionComposedText = (option: IDropdownOption<string>) => {
  const payload = option.payload as { composed?: unknown } | undefined
  const payloadText = typeof payload?.composed === 'string' ? payload.composed : undefined

  if (payloadText) {
    return payloadText
  }

  return typeof option.label === 'string' ? option.label : undefined
}

function ConsignorDropdown(props: IConsignorDropdownProps) {
  const {
    name,
    label,
    placeholder,
    currentText,
    options,
    position = 'modal',
    context,
    onTextChange,
    onOpen,
    dropdownProps,
    dropdownFooter,
  } = props

  const { userStores } = useUserStores()
  const [recentlyAddedStore, setRecentlyAddedStore] = useState<IStoreWithComment | undefined>(undefined)
  const [selectedValue, setSelectedValue] = useState<string | undefined>(undefined)
  const [displayText, setDisplayText] = useState<string>(typeof currentText === 'string' ? currentText : '')
  const localFieldName = `${name}__localDropdown`

  const computedLabel = label ?? undefined
  const computedPlaceholder = placeholder ?? (typeof label === 'string' ? label : undefined)
  const dropdownAriaLabel = !computedLabel && typeof computedPlaceholder === 'string' ? computedPlaceholder : undefined

  const mergedDropdownProps = useMemo(() => {
    const baseProps: Omit<IDropdownProps<string>, 'name' | 'options' | 'onChange'> & {
      'aria-label'?: string
    } = {
      ...(dropdownProps ?? {}),
    }

    const mergedContainerClassName = ['consignorDropdown__field', baseProps.containerClassName].filter(Boolean).join(' ')

    baseProps.containerClassName = mergedContainerClassName

    if (dropdownAriaLabel && typeof baseProps['aria-label'] !== 'string') {
      baseProps['aria-label'] = dropdownAriaLabel
    }

    return baseProps
  }, [dropdownProps, dropdownAriaLabel])

  const optionItems: IDropdownOption<string>[] = useMemo(() => {
    const stores: IStoreWithComment[] = Array.isArray(userStores) ? [...userStores] : []

    const ensureRecentStorePresent = (items: IDropdownOption<string>[]) => {
      if (!recentlyAddedStore) {
        return items
      }

      const recentValue = String(recentlyAddedStore.location?.id || recentlyAddedStore.id)

      const recentComposed = normalizeWhitespace(buildComposedText(recentlyAddedStore))

      const alreadyExists = items.some(option => {
        if (String(option.value) === recentValue) {
          return true
        }

        const optionText = getOptionComposedText(option)

        if (!optionText) {
          return false
        }

        return normalizeWhitespace(optionText) === recentComposed
      })

      if (alreadyExists) {
        return items
      }

      return [createOptionFromStore(recentlyAddedStore), ...items]
    }

    if (Array.isArray(options) && options.length > 0) {
      const storeById = new Map<string, IStoreWithComment>()

      stores.forEach(store => {
        const storeIdKey = String(store.id)

        if (!storeById.has(storeIdKey)) {
          storeById.set(storeIdKey, store)
        }

        const locationId = store.location?.id

        if (locationId) {
          const locationKey = String(locationId)

          if (!storeById.has(locationKey)) {
            storeById.set(locationKey, store)
          }
        }
      })

      const preparedOptions = options.map(option => {
        const value = String(option.value)
        const store = storeById.get(value)

        if (store) {
          const fallbackLabel = typeof option.label === 'string' ? option.label : undefined

          return createOptionFromStore(store, fallbackLabel)
        }

        return {
          ...option,
          value,
        }
      })

      return ensureRecentStorePresent(preparedOptions)
    }

    const preparedFromStores = stores.map(store => createOptionFromStore(store))

    return ensureRecentStorePresent(preparedFromStores)
  }, [options, recentlyAddedStore, userStores])

  // Формируем итоговый список опций, при необходимости добавляя синтетическую, чтобы отобразить текущий текст
  const optionsWithSynthetic = useMemo(() => {
    const normalizedDisplay = normalizeWhitespace(displayText)
    const hasText = normalizedDisplay.length > 0

    const matchesExistingOption = optionItems.some(option => {
      const optionText = getOptionComposedText(option)

      if (!optionText) {
        return false
      }

      return normalizeWhitespace(optionText) === normalizedDisplay
    })

    if (!hasText) {
      return optionItems
    }

    if (selectedValue === SYNTHETIC_VALUE || !matchesExistingOption) {
      const synthetic: IDropdownOption<string> = {
        value: SYNTHETIC_VALUE,
        label: displayText,
        payload: {
          composed: displayText,
        },
      }

      if (optionItems.some(option => option.value === SYNTHETIC_VALUE)) {
        return optionItems.map(option => (option.value === SYNTHETIC_VALUE ? synthetic : option))
      }

      return [synthetic, ...optionItems]
    }

    return optionItems
  }, [optionItems, selectedValue, displayText])

  // Пытаемся заранее выбрать опцию на основании текущего текста
  useEffect(() => {
    const normalizedDisplay = normalizeWhitespace(displayText)
    const textToMatch = normalizeWhitespace(currentText) || normalizedDisplay
    const hasDisplayText = Boolean(normalizedDisplay)
    const hasExistingSelection = typeof selectedValue === 'string' && selectedValue.length > 0

    if (!hasDisplayText) {
      if (hasExistingSelection) {
        setSelectedValue(undefined)
      }

      return
    }

    const hasStores = Array.isArray(userStores) && userStores.length > 0

    if (!hasStores) {
      if (!hasExistingSelection) {
        setSelectedValue(SYNTHETIC_VALUE)
      }

      return
    }

    const matched = userStores.find(store => normalizeWhitespace(buildComposedText(store as IStoreWithComment)) === textToMatch)

    if (matched) {
      const nextValue = String(matched.location?.id || matched.id || '') || SYNTHETIC_VALUE

      if (nextValue !== selectedValue) {
        setSelectedValue(nextValue)
      }

      return
    }

    if (selectedValue !== SYNTHETIC_VALUE) {
      setSelectedValue(SYNTHETIC_VALUE)
    }
  }, [currentText, displayText, selectedValue, userStores])

  const handleStoreChange = useCallback(
    (location: IStoreLocation) => {
      const store = (userStores.find(el => el.id === location?.id) || userStores.find(el => el.location?.id === location?.id)) as
        | IStoreWithComment
        | undefined

      if (!store) {
        return
      }

      const composed = buildComposedText(store)

      onTextChange?.(composed)
      setDisplayText(composed)

      const valueToSet = String(location?.id || store.id || '') || SYNTHETIC_VALUE

      if (selectedValue !== valueToSet) {
        setSelectedValue(valueToSet)
      }
    },
    [userStores, onTextChange, selectedValue],
  )

  const onDropdownChange: TOnChangeDropdownItem<string> = value => {
    if (value === SYNTHETIC_VALUE) {
      // Выбор синтетической опции просто сохраняет текущий текст
      setSelectedValue(SYNTHETIC_VALUE)

      return
    }

    const store = (userStores.find(el => el.location?.id === value) || userStores.find(el => el.id === value)) as
      | IStoreWithComment
      | undefined

    if (store) {
      handleStoreChange(store.location as unknown as IStoreLocation)

      return
    }

    // Резервный вариант: берем текст из списка опций, если не удалось найти магазин
    const opt = optionsWithSynthetic.find(o => String(o.value) === String(value))
    const optionLabel = opt ? getOptionComposedText(opt) || '' : ''

    if (optionLabel) {
      onTextChange?.(optionLabel)
      setDisplayText(optionLabel)
      setSelectedValue(SYNTHETIC_VALUE)

      return
    }

    // В крайнем случае оставляем выбранным само значение; отображение возьмется из displayText
    setSelectedValue(String(value))
  }

  // Поддерживаем displayText в актуальном состоянии относительно внешнего currentText
  useEffect(() => {
    const next = typeof currentText === 'string' ? currentText : ''
    if (next !== displayText) setDisplayText(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentText])

  return (
    <div className="consignorDropdown">
      <PoleUIDropdown<string>
        name={localFieldName}
        options={optionsWithSynthetic as IDropdownOption<string>[]}
        value={selectedValue}
        onChange={onDropdownChange}
        label={computedLabel as string | undefined}
        placeholder={computedPlaceholder as string | undefined}
        SelectedValue={({ value }) => {
          const text = typeof displayText === 'string' ? displayText.trim() : ''

          return <span>{text || String(value || '')}</span>
        }}
        onOpen={onOpen}
        DropdownListFooter={({ onClose }: TDropdownListFooterProps) => {
          if (typeof dropdownFooter === 'function') return dropdownFooter({ onClose })

          const handleClick = async () => {
            onClose()

            return true
          }

          return (
            <AddingLocationButton
              context={context}
              position={position}
              onClick={handleClick}
              onStoreAdded={(store: unknown) => {
                const s = store as IStoreWithComment
                setRecentlyAddedStore(s)

                const composed = buildComposedText(s)
                onTextChange?.(composed)

                const valueToSet = String(s.location?.id || s.id || '') || SYNTHETIC_VALUE
                setSelectedValue(valueToSet)
                setDisplayText(composed)
              }}
              sourceField={localFieldName}
              shouldSyncChosenAddress={false}
              restoreParentModalOnSuccess
            />
          )
        }}
        {...mergedDropdownProps}
      />
    </div>
  )
}

export default ConsignorDropdown
